import React, { useState, useEffect } from 'react'
import { backendService } from '../services/backendService'
import { logInfo } from '../utils/errorLogger'

interface PatientData {
  name: string
  dateOfBirth: string
  email: string
  phone: string
  medicalConditions: string
  medicalNotes: string
}

interface PatientRegistrationModalProps {
  show: boolean
  onClose: () => void
  doctorId: string | undefined
  doctorName: string | undefined
  onPatientAdded: () => Promise<void>
}

const STORAGE_KEY = 'patientRegistrationDraft'

const PatientRegistrationModal: React.FC<PatientRegistrationModalProps> = ({
  show,
  onClose,
  doctorId,
  doctorName,
  onPatientAdded,
}) => {
  const [patientData, setPatientData] = useState<PatientData>({
    name: '',
    dateOfBirth: '',
    email: '',
    phone: '',
    medicalConditions: '',
    medicalNotes: '',
  })
  const [draftLoaded, setDraftLoaded] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const assignedDoctorName = doctorName || 'Unknown Doctor'

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setPatientData(prev => ({ ...prev, [name]: value }))
  }

  const resetForm = () => {
    setPatientData({
      name: '',
      dateOfBirth: '',
      email: '',
      phone: '',
      medicalConditions: '',
      medicalNotes: '',
    })
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!patientData.name.trim()) {
      alert('Patient name is required')
      return
    }
    if (!doctorId) {
      alert('No doctor assigned')
      return
    }

    setSubmitting(true)
    try {
      const patientCreateData = {
        name: patientData.name.trim(),
        dateOfBirth: patientData.dateOfBirth || undefined,
        contactInfo: {} as { [key: string]: string },
        medicalInfo: {
          conditions: patientData.medicalConditions
            ? patientData.medicalConditions.split(',').map(c => c.trim())
            : [],
          notes: patientData.medicalNotes || undefined,
        },
      }
      if (patientData.email) patientCreateData.contactInfo.email = patientData.email
      if (patientData.phone) patientCreateData.contactInfo.phone = patientData.phone

      await backendService.addPatient(doctorId, patientCreateData)
      logInfo(`Patient added: ${patientData.name}`, 'Library Manager')
      sessionStorage.removeItem(STORAGE_KEY)
      resetForm()
      onClose()
      await onPatientAdded()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add patient'
      console.error('Patient registration error:', msg)
      alert(`Failed to register patient: ${msg}`)
    } finally {
      setSubmitting(false)
    }
  }

  // Load draft on mount
  useEffect(() => {
    if (show) {
      try {
        const saved = sessionStorage.getItem(STORAGE_KEY)
        if (saved) {
          setPatientData(prev => ({ ...prev, ...JSON.parse(saved) }))
        }
      } catch { /* ignore */ }
      setDraftLoaded(true)
    } else {
      setDraftLoaded(false)
    }
  }, [show])

  // Persist draft on change
  useEffect(() => {
    if (!draftLoaded) return
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(patientData))
    } catch { /* ignore */ }
  }, [patientData, draftLoaded])

  if (!show) return null

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content patient-registration-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Patient Registration</h3>
          <button type="button" className="modal-close" onClick={handleClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="modal-body">
            <div className="form-group">
              <label>Assigned Doctor:</label>
              <div className="selected-doctor">{assignedDoctorName}</div>
            </div>

            <div className="form-section">
              <h4>Personal Information</h4>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="patientName">Full Name *</label>
                  <input
                    id="patientName"
                    name="name"
                    type="text"
                    value={patientData.name}
                    onChange={handleInputChange}
                    placeholder="Enter patient's full name"
                    required
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="dateOfBirth">Date of Birth</label>
                  <input
                    id="dateOfBirth"
                    name="dateOfBirth"
                    type="date"
                    value={patientData.dateOfBirth}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h4>Contact Information</h4>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={patientData.email}
                    onChange={handleInputChange}
                    placeholder="patient@example.com"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={patientData.phone}
                    onChange={handleInputChange}
                    placeholder="+351 123 456 789"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h4>Medical Information</h4>
              <div className="form-group">
                <label htmlFor="medicalConditions">Medical Conditions</label>
                <input
                  id="medicalConditions"
                  name="medicalConditions"
                  type="text"
                  value={patientData.medicalConditions}
                  onChange={handleInputChange}
                  placeholder="Diabetes, Hypertension, etc. (comma-separated)"
                />
                <small className="form-help">Enter conditions separated by commas</small>
              </div>
              <div className="form-group">
                <label htmlFor="medicalNotes">Medical Notes</label>
                <textarea
                  id="medicalNotes"
                  name="medicalNotes"
                  value={patientData.medicalNotes}
                  onChange={handleInputChange}
                  placeholder="Additional medical information, allergies, special considerations..."
                  rows={3}
                />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={handleClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={!patientData.name.trim() || submitting}>
              {submitting ? 'Registering...' : 'Register Patient'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PatientRegistrationModal
